import React, { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Clock, MessageCircle, Send } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { serviceCatalog } from "@/data/services";
import { HERO_BACKGROUNDS } from "@/data/projectImages";
import PageHero from "@/components/PageHero";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t, lang } = useT();
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang });
      toast.success(t.contact.form_success);
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.form_error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        overline={t.contact.overline}
        title={t.contact.title}
        subtitle={t.contact.subtitle}
        backgroundImage={HERO_BACKGROUNDS.contact}
      />

      <section className="py-12 sm:py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 sm:gap-12">
          {/* Form */}
          <form onSubmit={onSubmit} className="lg:col-span-7 space-y-5" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-bold text-zinc-700 tracking-widest uppercase mb-2">{t.contact.form_name} *</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={form.name}
                  onChange={onChange}
                  className="w-full border border-zinc-300 px-4 py-3 text-sm focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-colors"
                  data-testid="contact-input-name"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-700 tracking-widest uppercase mb-2">{t.contact.form_email} *</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={form.email}
                  onChange={onChange}
                  className="w-full border border-zinc-300 px-4 py-3 text-sm focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-colors"
                  data-testid="contact-input-email"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-700 tracking-widest uppercase mb-2">{t.contact.form_phone}</label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  className="w-full border border-zinc-300 px-4 py-3 text-sm focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-colors"
                  data-testid="contact-input-phone"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-zinc-700 tracking-widest uppercase mb-2">{t.contact.form_service}</label>
                <select
                  name="service"
                  value={form.service}
                  onChange={onChange}
                  className="w-full border border-zinc-300 px-4 py-3 text-sm focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-colors bg-white"
                  data-testid="contact-input-service"
                >
                  <option value="">{t.contact.form_service_placeholder}</option>
                  {serviceCatalog.map((s) => (
                    <option key={s.slug} value={t.services.items[s.key].title}>
                      {t.services.items[s.key].title}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-zinc-700 tracking-widest uppercase mb-2">{t.contact.form_message} *</label>
              <textarea
                name="message"
                required
                rows={6}
                value={form.message}
                onChange={onChange}
                className="w-full border border-zinc-300 px-4 py-3 text-sm focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 transition-colors resize-none"
                data-testid="contact-input-message"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="contact-submit"
            >
              {sending ? t.contact.form_sending : t.contact.form_send}
              <Send size={16} />
            </button>
          </form>

          {/* Info */}
          <aside className="lg:col-span-5 lg:border-l lg:border-zinc-200 lg:pl-12">
            <h3 className="font-display text-2xl font-bold text-zinc-900">{t.contact.info_title}</h3>
            <div className="mt-8 space-y-6">
              <div className="flex gap-4">
                <MapPin size={20} className="text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-zinc-500">{t.contact.info_address}</div>
                  <div className="mt-1 text-sm text-zinc-800 font-medium">{t.common.address}</div>
                </div>
              </div>
              <div className="flex gap-4">
                <Phone size={20} className="text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-zinc-500">{t.contact.info_phone}</div>
                  <a href="tel:+41796070148" className="mt-1 block text-sm text-zinc-800 font-medium hover:text-red-600" data-testid="info-phone">079 607 01 48</a>
                </div>
              </div>
              <div className="flex gap-4">
                <Mail size={20} className="text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-zinc-500">{t.contact.info_email}</div>
                  <a href="mailto:info@sf-maler.ch" className="mt-1 block text-sm text-zinc-800 font-medium hover:text-red-600" data-testid="info-email">info@sf-maler.ch</a>
                </div>
              </div>
              <div className="flex gap-4">
                <Clock size={20} className="text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-zinc-500">{t.contact.info_hours}</div>
                  <div className="mt-1 text-sm text-zinc-800 font-medium">{t.contact.info_hours_value}</div>
                </div>
              </div>
            </div>

            <a
              href="https://wa.me/41796070148"
              target="_blank"
              rel="noreferrer"
              className="mt-10 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-4 font-bold hover:opacity-90 transition-opacity"
              data-testid="info-whatsapp"
            >
              <MessageCircle size={18} fill="white" strokeWidth={1.5} /> {t.contact.whatsapp}
            </a>

            {/* Map */}
            <div className="mt-8 border border-zinc-200 overflow-hidden">
              <iframe
                title="SF-Maler Basel"
                src="https://www.google.com/maps?q=Wollbacherstrasse+35,+4058+Basel&output=embed"
                className="w-full h-64 border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default Contact;
